import flattenDeepByIteratee from '@components/UI-Components/HierarchyWiseItemsTable/utils/flatten-deep-by-iteratee'
import { OrderType } from '@modules/common'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import {
  CATEGORY_UI_SEPERATOR,
  PRODUCT_REMOVE_LOADER_KEY
} from '@modules/order/constants/uiConstants'
import { View } from '@modules/order/constants/views'
import getActivityHierarchyMap from '@modules/order/utils/get-activity-hierarchy-map'
import { CancellableRequest } from '@services/HttpService'
import CommentService from '@services/commentService'
import { stores } from '@stores'
import { nav } from '@stores/nav'
import {
  HIERARCHY_API_KEY,
  PaginatedHierarchyItemsDataStore
} from '@stores/paginated-hierarchy-items/PaginatedHierarchyItemsDataStore'
import { getEnumValue } from '@utils/get-enum-value'
import sumBy from 'lodash/sumBy'
import find from 'lodash/find'
import intersectionBy from 'lodash/intersectionBy'
import join from 'lodash/join'
import keyBy from 'lodash/keyBy'
import map from 'lodash/map'
import merge from 'lodash/merge'
import omit from 'lodash/omit'
import reduce from 'lodash/reduce'
import set from 'lodash/set'
import forEach from 'lodash/forEach'
import { action, computed, observable, reaction, toJS } from 'mobx'
import ListDataService from '../../../services/ListDataService'
import { orderKPIGlobalTopBarStore } from '../../OrderKPIGlobalTopBarStore'
import { ListTableView } from './ListViewTableService'

export enum PRODUCT_OPERATIONS_LOADERS {
  UPDATING_DRAFT_QUANTITY = 'UPDATING_DRAFT_QUANTITY',
  BOOK_DOOR = 'BOOK_DOOR',
  UNBOOK_DOOR = 'UNBOOK_DOOR',
  UPDATING_DOOR_DRAFT_QUANTITY = 'UPDATING_DOOR_DRAFT_QUANTITY',
  BOOKING_ALL_DOORS = 'BOOKING_ALL_DOORS',
  UNBOOKING_ALL_DOORS = 'UNBOOKING_ALL_DOORS',
  BOOK_UNBOOK_IN_PROGRESS = 'BOOK_UNBOOK_IN_PROGRESS',
  UPDATING_CLIENT_ORDER_DRAFT_QUANTITY = 'UPDATING_CLIENT_ORDER_DRAFT_QUANTITY',
  SPLITTING_CLIENT_ORDER_QUANTITY = 'SPLITTING_CLIENT_ORDER_QUANTITY',
  SPLITTING_CLIENT_ORDER_SIZE_QUANTITY = 'SPLITTING_CLIENT_ORDER_SIZE_QUANTITY',
  UPDATING_COMMENT = 'UPDATING_COMMENT'
}

export class ListDataStore extends PaginatedHierarchyItemsDataStore {
  private overviewReactions = []
  @observable.shallow hierarchyWithoutFilters = []

  private service = new ListDataService()
  private commentService = new CommentService()

  fetchItemsFromAPI(
    fromIndex: number,
    toIndex: number
  ): CancellableRequest<any> | Promise<any[]> {
    const {
      queryParams: {
        buyingSessionGroupId,
        orderId,
        orderType,
        subSection,
        clientType,
        view
      }
    } = nav
    const { activity, family, line } = getActivityHierarchyMap(
      Array.from(this.hierarchyInRange(fromIndex, toIndex, this.heirarchy, 0))
    )

    const filterData = {
      ...this.mapFiltersToIds(this.filterStore.appliedFilters),
      activityName: [activity.name],
      ...(family ? { familyName: [family.name] } : {}),
      ...(line ? { lineName: [line.name] } : {})
    }

    /**
     * include qty should always be true for list view
     * include summary should be true while landing on rtl list
     * or when subsection says so
     */
    const includeTotalQty = true
    const includeSummary =
      subSection === ListTableView.Summary ||
      (view === View.List &&
        clientType === getEnumValue(VIPClientType.Internal) &&
        subSection === undefined)

    const productsByHierarchy = this.service.fetchProductsByHierarchy(
      buyingSessionGroupId,
      orderId,
      orderType,
      filterData,
      this.searchKey,
      includeTotalQty,
      includeSummary,
      this.sortStore.isDefaultSelected && this.sortStore.appliedSortOptions
    )

    return productsByHierarchy
  }

  fetchHierarchyFromAPI(): CancellableRequest<any> | Promise<any> {
    const {
      queryParams: { buyingSessionGroupId, orderId, orderType, clientOrderId, subSection }
    } = nav
    if (!orderId) return undefined
    return this.service.fetchHierarchy(
      buyingSessionGroupId,
      orderId,
      orderType,
      this.mapFiltersToIds(this.filterStore.appliedFilters),
      this.searchKey,
      clientOrderId,
      true,
      subSection === ListTableView.Quantity || subSection === undefined,
      subSection === ListTableView.Summary || subSection === undefined,
      this.sortStore.isDefaultSelected && this.sortStore.appliedSortOptions
    )
  }

  fetchFilterAttributesFromAPI(): CancellableRequest<any> | Promise<any> {
    const {
      queryParams: { buyingSessionGroupId, orderId, orderType }
    } = nav
    if (!orderId) return undefined
    return this.service.fetchFilterAttributes(buyingSessionGroupId, orderId, orderType)
  }

  removeItemAPI(productId: string) {
    const {
      nav: {
        queryParams: { orderId, orderType }
      }
    } = stores
    return this.service.removeProductToOrder(
      orderId,
      productId,
      orderType as OrderType,
      true,
      true,
      this.mapFiltersToIds(this.filterStore.appliedFilters),
      this.searchKey
    )
  }

  replaceDataServiceWith(service) {
    this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
    this.itemIndexObjectStore.clearAll()
    this.heirarchy = []
    this.service = service
  }

  @action
  async init() {
    await super.init()
    this.overviewReactions.push(
      reaction(
        () => this.heirarchy,
        () => {
          if (!this.filterStore.appliedFilters?.length && !this.searchKey) {
            this.hierarchyWithoutFilters = toJS(this.heirarchy, {
              recurseEverything: true
            })
          }
        }
      )
    )
  }

  @action
  async updateDraftQuantity(productId, payload, pathToUpdate, rollUp) {
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    try {
      this.itemIndexObjectStore.setRangeLoading(
        productIndex,
        productIndex + 1,
        PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
        true
      )
      const {
        queryParams: { orderId, subSection }
      } = nav
      const res = await this.service.updateProductDraftQuantity(
        productId,
        orderId,
        payload,
        this.mapFiltersToIds(this.filterStore.appliedFilters),
        this.searchKey,
        subSection === ListTableView.Summary || subSection === undefined,
        rollUp
      )
      const quantityPath = `${pathToUpdate}.totalByProductQuantity`
      const valuePath = `${pathToUpdate}.totalValueByProductQuantity`
      const gradingAttributePath = `${pathToUpdate}.gradingAttributes`
      const salesQuantityPath = `${pathToUpdate}.salesQuantity`
      const avgWeekSalesPath = `${pathToUpdate}.avgWeeklySales`
      const avgDepthPath = `${pathToUpdate}.avgDepth`
      const productById = this.itemIndexObjectStore.idObjectMap.get(productId)

      set(
        this.itemIndexObjectStore.idObjectMap.get(productId),
        quantityPath,
        res?.product?.totalByProductQuantity
      )
      set(
        this.itemIndexObjectStore.idObjectMap.get(productId),
        valuePath,
        res?.product?.totalValueByProductQuantity
      )
      set(
        this.itemIndexObjectStore.idObjectMap.get(productId),
        gradingAttributePath,
        res?.product?.gradingAttributes || {}
      )
      set(
        this.itemIndexObjectStore.idObjectMap.get(productId),
        avgWeekSalesPath,
        res?.product?.averageWeeklySales || 0
      )
      set(
        this.itemIndexObjectStore.idObjectMap.get(productId),
        salesQuantityPath,
        res?.product?.salesQuantity || 0
      )
      set(
        this.itemIndexObjectStore.idObjectMap.get(productId),
        avgDepthPath,
        res?.product?.avgDepth || 0
      )
      this.itemIndexObjectStore.idObjectMap.set(productId, productById)

      const productByIndex = this.itemIndexObjectStore.indexObjectMap.get(productIndex)
      set(
        this.itemIndexObjectStore.indexObjectMap.get(productIndex),
        quantityPath,
        res?.product?.totalByProductQuantity
      )
      set(
        this.itemIndexObjectStore.indexObjectMap.get(productIndex),
        valuePath,
        res?.product?.totalValueByProductQuantity
      )
      set(
        this.itemIndexObjectStore.indexObjectMap.get(productIndex),
        gradingAttributePath,
        res?.product?.gradingAttributes || {}
      )
      set(
        this.itemIndexObjectStore.indexObjectMap.get(productIndex),
        avgWeekSalesPath,
        res?.product?.averageWeeklySales || 0
      )
      set(
        this.itemIndexObjectStore.indexObjectMap.get(productIndex),
        salesQuantityPath,
        res?.product?.salesQuantity || 0
      )
      set(
        this.itemIndexObjectStore.indexObjectMap.get(productIndex),
        avgDepthPath,
        res?.product?.avgDepth || 0
      )
      this.itemIndexObjectStore.indexObjectMap.set(productIndex, productByIndex)
      this.heirarchy = this.mergeHierarchies(
        res.hierarchyKPIs,
        this.heirarchy,
        res?.orderTotalKpis ?? {}
      )
      const { lineItems } = res
      if (rollUp) {
        for (let lineItem of lineItems) {
          const { buyingSessionProductId } = lineItem
          const product =
            this.itemIndexObjectStore.idObjectMap.get(buyingSessionProductId)
          const productIndex =
            this.itemIndexObjectStore.getIndexByKey(buyingSessionProductId)
          const doorOrderProduct =
            product?.quantity?.doorProducts?.[lineItem?.doorOrderId]
          if (doorOrderProduct) {
            const updatedProduct = {
              ...product,
              quantity: {
                ...product.quantity,
                totalByDraftQuantity: res?.product?.totalByDraftQuantity,
                totalBySizeQuantity: res?.product?.totalBySizeQuantity,
                totalValueByDraftQuantity: res?.product?.totalValueByDraftQuantity,
                totalValueBySizeQuantity: res?.product?.totalValueBySizeQuantity
              }
            }
            updatedProduct.quantity.doorProducts[lineItem?.doorOrderId] = {
              ...updatedProduct.quantity.doorProducts[lineItem?.doorOrderId],
              totalByDraftQuantity: lineItem.totalByDraftQuantity,
              totalBySizeQuantity: lineItem.totalBySizeQuantity,
              totalValueByDraftQuantity: lineItem.totalValueByDraftQuantity,
              totalValueBySizeQuantity: lineItem.totalValueBySizeQuantity
            }
            this.itemIndexObjectStore.idObjectMap.set(product.id, updatedProduct)
            this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
          }
        }
      }
      this.itemIndexObjectStore.setRangeLoading(
        productIndex,
        productIndex + 1,
        PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
        false
      )
      // Update Top Bar with updated KPI values
      const { clientOrder, doorOrder } = res
      orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder, doorOrder })
      this.setQuantityUpdated(true)
    } catch (e) {
      this.itemIndexObjectStore.setRangeLoading(
        productIndex,
        productIndex + 1,
        PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
        false
      )
    }
  }

  @action
  async rollUpAllQuantities() {
    try {
      const {
        queryParams: { orderId }
      } = nav
      const buyingSessionProductIds = this.products
      const {
        products,
        lineItems,
        hierarchyKPIs,
        orderTotalKpis,
        clientOrder,
        doorOrder
      } = await this.service.rollUpAllQuantities(
        orderId,
        buyingSessionProductIds,
        this.mapFiltersToIds(this.filterStore.appliedFilters),
        this.searchKey
      )

      for (let product of products) {
        const { buyingSessionProductId } = product
        const productIndex =
          this.itemIndexObjectStore.getIndexByKey(buyingSessionProductId)
        this.itemIndexObjectStore.setRangeLoading(
          productIndex,
          productIndex + 1,
          PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
          true
        )
        const kpisToBeUpdated = [
          'totalByProductQuantity',
          'totalValueByProductQuantity',
          'gradingAttributes',
          'totalByDraftQuantity',
          'totalValueByDraftQuantity',
          'totalBySizeQuantity',
          'totalValueBySizeQuantity',
          'avgDepth'
        ]
        const productById =
          this.itemIndexObjectStore.idObjectMap.get(buyingSessionProductId)

        const pathToUpdate = 'quantity'

        forEach(kpisToBeUpdated, kpi => {
          set(
            this.itemIndexObjectStore.idObjectMap.get(buyingSessionProductId),
            `${pathToUpdate}.${kpi}`,
            product?.[kpi]
          )
        })

        this.itemIndexObjectStore.idObjectMap.set(buyingSessionProductId, productById)
        const productByIndex = this.itemIndexObjectStore.indexObjectMap.get(productIndex)

        forEach(kpisToBeUpdated, kpi => {
          set(
            this.itemIndexObjectStore.indexObjectMap.get(productIndex),
            `${pathToUpdate}.${kpi}`,
            product?.[kpi]
          )
        })
        this.itemIndexObjectStore.indexObjectMap.set(productIndex, productByIndex)
        this.itemIndexObjectStore.setRangeLoading(
          productIndex,
          productIndex + 1,
          PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
          false
        )
      }

      for (let lineItem of lineItems) {
        const { buyingSessionProductId } = lineItem
        const product = this.itemIndexObjectStore.idObjectMap.get(buyingSessionProductId)
        const productIndex =
          this.itemIndexObjectStore.getIndexByKey(buyingSessionProductId)
        const doorOrderProduct = product?.quantity?.doorProducts?.[lineItem?.doorOrderId]
        if (doorOrderProduct) {
          product.quantity.doorProducts[lineItem?.doorOrderId] = {
            ...product.quantity.doorProducts[lineItem?.doorOrderId],
            totalByDraftQuantity: lineItem.totalByDraftQuantity,
            totalBySizeQuantity: lineItem.totalBySizeQuantity,
            totalValueByDraftQuantity: lineItem.totalValueByDraftQuantity,
            totalValueBySizeQuantity: lineItem.totalValueBySizeQuantity
          }
          this.itemIndexObjectStore.idObjectMap.set(product.id, product)
          this.itemIndexObjectStore.indexObjectMap.set(productIndex, product)
        }
      }

      this.heirarchy = this.mergeHierarchies(
        hierarchyKPIs,
        this.heirarchy,
        orderTotalKpis
      )
      // Update Top Bar with updated KPI values
      orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder, doorOrder })
      this.setQuantityUpdated(true)
    } catch (e) {}
  }

  /**
   *
   * @param hierarchyToMergeIn - received from server
   * @param hierarchyToMergeFrom - stored in current data
   * @returns
   */
  private mergeHierarchies(
    hierarchyToMergeIn,
    hierarchyToMergeFrom,
    orderTotalKpis = {},
    mergeFunc?: (hierarchyObjToMergeIn: any, hierarchyObjToMergeFrom: any) => any
  ) {
    const hierarchyToMergeInMap = keyBy(hierarchyToMergeIn, 'id')
    return (intersectionBy(hierarchyToMergeFrom, hierarchyToMergeIn, 'id') as any[]).map(
      (hierarchyObj, index) => ({
        ...(mergeFunc
          ? mergeFunc(hierarchyToMergeInMap[hierarchyObj.id], hierarchyObj)
          : hierarchyToMergeInMap[hierarchyObj.id]),
        totalProducts: hierarchyToMergeInMap[hierarchyObj.id].kpis.totalProducts,
        name: hierarchyObj.name,
        orderTotalKpis: !index ? orderTotalKpis : {},
        ...(hierarchyObj.children?.length
          ? {
              children: this.mergeHierarchies(
                flattenDeepByIteratee(
                  hierarchyToMergeInMap[hierarchyObj.id].children,
                  item => item.children
                ),
                hierarchyObj.children
              )
            }
          : { children: [] })
      })
    )
  }

  @action
  destroy() {
    super.destroy()
    this.overviewReactions.forEach(disposer => disposer())
    this.overviewReactions = []
  }

  @computed get activities() {
    const hierarchy = map(this.hierarchyWithoutFilters, 'name')
    return join(hierarchy, CATEGORY_UI_SEPERATOR)
  }

  @computed get products() {
    const productIds = []
    const iterator = this.itemIndexObjectStore.idObjectMap.entries()
    let nextRes = iterator.next()
    while (!nextRes.done) {
      productIds.push(nextRes.value[0])
      nextRes = iterator.next()
    }
    return productIds
  }

  @action
  bookUnBookDoorsForSummary = async (
    clientOrderId,
    productId,
    doorOrderIds,
    isBook,
    removeIfEmpty
  ) => {
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    try {
      let response
      // get product maps to update
      let updatedDoorProducts = product?.summary?.doorProducts || {}

      if (doorOrderIds.length) {
        doorOrderIds.forEach(doorOrderId => {
          this.itemIndexObjectStore.setObjectLoading(
            productIndex,
            `${PRODUCT_OPERATIONS_LOADERS.BOOK_DOOR}_${doorOrderId}`,
            false
          )
        })
      } else {
        this.itemIndexObjectStore.setObjectLoading(
          productIndex,
          PRODUCT_OPERATIONS_LOADERS.BOOKING_ALL_DOORS,
          true
        )
      }

      if (isBook) {
        response = await this.service.bookDoorOrder(
          clientOrderId,
          productId,
          doorOrderIds
        )
        // push the newly created door product
        updatedDoorProducts = {
          ...updatedDoorProducts,
          ...this.createDoorProducts(response?.lineItemsCreated || [])
        }

        const updatedProduct = {
          ...product,
          summary: {
            ...product.summary,
            doorProducts: updatedDoorProducts
          }
        }
        this.itemIndexObjectStore.idObjectMap.set(productId, updatedProduct)
        this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
        // Update Top Bar with updated KPI values
        orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({
          clientOrder: {
            ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.clientOrder,
            ...response.clientOrder
          },
          doorOrder: {
            ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.doorOrder,
            ...response.doorOrder
          }
        })
      } else {
        response = await this.service.unbookDoors(
          clientOrderId,
          productId,
          doorOrderIds,
          this.mapFiltersToIds(this.filterStore.appliedFilters),
          this.searchKey
        )

        if (response) {
          // remove the deleted door product
          if (doorOrderIds.length > 0) {
            updatedDoorProducts = { ...updatedDoorProducts }
            updatedDoorProducts = omit(updatedDoorProducts, doorOrderIds)
          } else {
            updatedDoorProducts = {}
          }
        }

        this.heirarchy = this.mergeHierarchies(
          response?.hierarchyKPIs,
          this.heirarchy,
          response?.orderTotalKpis ?? {}
        )
        // set maps with the updated door products
        if (0 === Object.keys(updatedDoorProducts || {})?.length && removeIfEmpty) {
          // if only one door order product is present delete the row
          this.itemIndexObjectStore.clearDataByRange(productIndex, productIndex)
        } else {
          const updatedProduct = {
            ...product,
            summary: {
              ...product.summary,
              ...(response?.product
                ? response.product
                : {
                    totalByDraftQuantity: 0,
                    totalBySizeQuantity: 0,
                    totalValueByDraftQuantity: 0,
                    totalValueBySizeQuantity: 0
                  }),
              doorProducts: updatedDoorProducts
            }
          }
          this.itemIndexObjectStore.idObjectMap.set(productId, updatedProduct)
          this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
        }

        // Update Top Bar with updated KPI values
        const { clientOrder, doorOrder } = response
        orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder, doorOrder })
        this.setQuantityUpdated(true)
      }
    } finally {
      if (doorOrderIds.length) {
        doorOrderIds.forEach(doorOrderId => {
          this.itemIndexObjectStore.setObjectLoading(
            productIndex,
            `${PRODUCT_OPERATIONS_LOADERS.BOOK_DOOR}_${doorOrderId}`,
            false
          )
          this.itemIndexObjectStore.setObjectLoading(
            productIndex,
            `${PRODUCT_OPERATIONS_LOADERS.UNBOOK_DOOR}_${doorOrderId}`,
            false
          )
        })
      } else {
        this.itemIndexObjectStore.setObjectLoading(
          productIndex,
          PRODUCT_OPERATIONS_LOADERS.BOOKING_ALL_DOORS,
          false
        )
      }
    }
  }

  // *********** Quantity Sub Section ***********
  @action
  async updateDoorOrderDraftQuantity(
    productId: string,
    payload: any,
    { isSizeUpdate }: { isSizeUpdate?: boolean } = {} // options
  ) {
    const {
      queryParams: { orderId: navOrderId, orderType, clientOrderId: navClientOrderId }
    } = nav
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    const clientOrderId = orderType === OrderType.Door ? navClientOrderId : navOrderId
    const kpisBy = orderType === OrderType.Door ? 'door' : 'client'

    try {
      const res = isSizeUpdate
        ? await this.service.updateSizeQuantity(
            {
              clientOrderId,
              bspId: productId,
              doorOrderId: payload.doorOrderId,
              sizeId: payload.sizeId
            },
            { quantity: payload.newValue },
            this.mapFiltersToIds(this.filterStore.appliedFilters),
            this.searchKey
          )
        : await this.service.updateDoorOrderQuantity(
            productId,
            clientOrderId,
            payload,
            kpisBy,
            this.mapFiltersToIds(this.filterStore.appliedFilters),
            this.searchKey
          )

      const doorOrderProduct = product?.quantity?.doorProducts?.[payload?.doorOrderId]
      if (doorOrderProduct) {
        const updatedProduct = {
          ...product,
          quantity: {
            ...product.quantity,
            totalByDraftQuantity:
              orderType === OrderType.Door
                ? res.lineItem?.totalByDraftQuantity
                : res.product?.totalByDraftQuantity,
            totalBySizeQuantity:
              orderType === OrderType.Door
                ? res.lineItem?.totalBySizeQuantity
                : res.product?.totalBySizeQuantity,
            totalValueByDraftQuantity:
              orderType === OrderType.Door
                ? res.lineItem?.totalValueByDraftQuantity
                : res.product?.totalValueByDraftQuantity,
            totalValueBySizeQuantity:
              orderType === OrderType.Door
                ? res.lineItem?.totalValueBySizeQuantity
                : res.product?.totalValueBySizeQuantity
          }
        }
        updatedProduct.quantity.doorProducts[payload?.doorOrderId] = {
          ...updatedProduct.quantity.doorProducts[payload?.doorOrderId],
          sizeQuantities: isSizeUpdate
            ? {
                ...updatedProduct.quantity.doorProducts[payload?.doorOrderId]
                  ?.sizeQuantities,
                [payload.sizeId]: {
                  sizeId: payload.sizeId,
                  quantity: payload.newValue
                }
              }
            : reduce(
                payload.sizeQuantities,
                (accumulator, sizeQuantity) => {
                  accumulator[sizeQuantity.sizeId] = sizeQuantity
                  return accumulator
                },
                {}
              ),
          totalByDraftQuantity: res.lineItem.totalByDraftQuantity,
          totalBySizeQuantity: res.lineItem.totalBySizeQuantity,
          totalValueByDraftQuantity: res.lineItem.totalValueByDraftQuantity,
          totalValueBySizeQuantity: res.lineItem.totalValueBySizeQuantity
        }
        this.itemIndexObjectStore.idObjectMap.set(product.id, updatedProduct)
        this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
        this.heirarchy = this.mergeHierarchies(
          res?.hierarchyKPIs,
          this.heirarchy,
          res?.orderTotalKpis
        )
        // Update Top Bar with updated KPI values
        const { clientOrder, doorOrder } = res
        orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder, doorOrder })
        this.setQuantityUpdated(true)
      }
    } catch (e) {
      const oldValue = toJS(product, { recurseEverything: true })
      this.itemIndexObjectStore.idObjectMap.set(product.id, oldValue)
      this.itemIndexObjectStore.indexObjectMap.set(productIndex, oldValue)
    }
  }

  /**
   *
   * @param newDoorKpis - received from server
   * @param oldHierarchy - stored in current data
   * @returns
   */
  private updateDoorTotalKpisCount(newDoorKpis, oldHierarchy) {
    return oldHierarchy.map((hierarchyObj, index) => {
      const dooKpis = !index
        ? merge(hierarchyObj?.orderTotalKpis?.doorKpis, newDoorKpis?.doorKpis)
        : {}
      return {
        ...hierarchyObj,
        orderTotalKpis: !index ? { ...hierarchyObj?.orderTotalKpis, dooKpis } : {}
      }
    })
  }

  // Quantity
  @action
  bookUnBookDoors = async (
    clientOrderId,
    productId,
    doorOrderIds,
    isBook,
    removeIfEmpty
  ) => {
    const {
      queryParams: { orderType, orderId, bspId }
    } = nav
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    try {
      let response
      // get product maps to update
      let updatedDoorProducts = product?.quantity?.doorProducts || {}
      if (doorOrderIds.length) {
        doorOrderIds.forEach(doorOrderId => {
          this.itemIndexObjectStore.setObjectLoading(
            productIndex,
            `${PRODUCT_OPERATIONS_LOADERS.BOOK_DOOR}_${doorOrderId}`,
            false
          )
        })
      } else {
        this.itemIndexObjectStore.setObjectLoading(
          productIndex,
          PRODUCT_OPERATIONS_LOADERS.BOOKING_ALL_DOORS,
          true
        )
      }
      if (isBook) {
        response = await this.service.bookDoorOrder(
          clientOrderId,
          productId,
          doorOrderIds
        )
        // push the newly created door product
        updatedDoorProducts = {
          ...updatedDoorProducts,
          ...this.createDoorProducts(response?.lineItemsCreated || [])
        }
        const updatedProduct = {
          ...product,
          quantity: {
            ...product.quantity,
            doorProducts: updatedDoorProducts
          }
        }
        this.heirarchy = this.updateDoorTotalKpisCount(
          response?.orderTotalKpis,
          this.heirarchy
        )
        this.itemIndexObjectStore.idObjectMap.set(productId, updatedProduct)
        this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
        // Update Top Bar with updated KPI values
        const clientOrder = response?.clientOrder
        const doorOrder = response?.doorOrder
        orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder, doorOrder })
      } else {
        response =
          orderType === OrderType.Door
            ? await this.service.unbookDoorForDoorOrder(
                orderId,
                productId,
                this.mapFiltersToIds(this.filterStore.appliedFilters),
                this.searchKey
              )
            : await this.service.unbookDoors(
                clientOrderId,
                productId,
                doorOrderIds,
                this.mapFiltersToIds(this.filterStore.appliedFilters),
                this.searchKey
              )
        if (response) {
          // remove the deleted door product
          if (doorOrderIds.length > 0) {
            updatedDoorProducts = { ...updatedDoorProducts }
            updatedDoorProducts = omit(updatedDoorProducts, doorOrderIds)
          } else {
            updatedDoorProducts = {}
          }
        }
        this.heirarchy =
          orderType === OrderType.Door && bspId
            ? this.heirarchy
            : this.mergeHierarchies(
                response?.hierarchyKPIs,
                this.heirarchy,
                response?.orderTotalKpis ?? {}
              )
        // set maps with the updated door products
        if (
          (0 === Object.keys(updatedDoorProducts || {})?.length && removeIfEmpty) ||
          orderType === OrderType.Door
        ) {
          // if only one door order product is present delete the row
          if (!bspId) {
            this.itemIndexObjectStore.clearDataByRange(productIndex, productIndex)
          } else {
            this.singleDoorResCache[bspId] = response
            const updatedProduct = {
              ...product,
              quantity: {
                ...product.quantity,
                doorProducts: {},
                totalByDraftQuantity: 0,
                totalBySizeQuantity: 0,
                totalByProductQuantity: 0,
                totalValueByDraftQuantity: 0,
                totalValueBySizeQuantity: 0,
                totalValueByProductQuantity: 0,
                gradingAttributes: {}
              }
            }
            this.itemIndexObjectStore.idObjectMap.set(productId, updatedProduct)
            this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
          }
        } else {
          const updatedProduct = {
            ...product,
            quantity: {
              ...product.quantity,
              ...(response?.product
                ? response.product
                : {
                    totalByDraftQuantity: 0,
                    totalBySizeQuantity: 0,
                    totalValueByDraftQuantity: 0,
                    totalValueBySizeQuantity: 0
                  }),
              gradingAttributes: { ...product.quantity.gradingAttributes },
              doorProducts: updatedDoorProducts
            }
          }
          this.itemIndexObjectStore.idObjectMap.set(productId, updatedProduct)
          this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
        }
        // Update Top Bar with updated KPI values
        const { clientOrder, doorOrder } = response
        orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder, doorOrder })
      }
    } finally {
      if (doorOrderIds.length) {
        doorOrderIds.forEach(doorOrderId => {
          this.itemIndexObjectStore.setObjectLoading(
            productIndex,
            `${PRODUCT_OPERATIONS_LOADERS.BOOK_DOOR}_${doorOrderId}`,
            false
          )
          this.itemIndexObjectStore.setObjectLoading(
            productIndex,
            `${PRODUCT_OPERATIONS_LOADERS.UNBOOK_DOOR}_${doorOrderId}`,
            false
          )
        })
      } else {
        this.itemIndexObjectStore.setObjectLoading(
          productIndex,
          PRODUCT_OPERATIONS_LOADERS.BOOKING_ALL_DOORS,
          false
        )
      }
    }
  }

  private createDoorProducts = (
    doorProductsFromResponse = []
    // zerothDoorProductPresent // for mapping currency, as we don't get currency from the api response
  ) => {
    return reduce(
      doorProductsFromResponse,
      (accumulator, doorProduct) => {
        accumulator[doorProduct.doorOrderId] = {
          doorId: doorProduct.doorId,
          doorOrderId: doorProduct.doorOrderId,
          totalByDraftQuantity: null,
          totalBySizeQuantity: null,
          totalValueByDraftQuantity: 0,
          totalValueBySizeQuantity: 0
          // not needed for now
          // totalSizedValueWithCurrency: zerothDoorProductPresent?.totalSizedValueWithCurrency,
          // totalDraftValueWithCurrency: zerothDoorProductPresent?.totalDraftValueWithCurrency
          // sizeQuantities: []
        }
        return accumulator
      },
      {}
    )
  }

  @action
  bulkUpdateDraftQuantity = async (clientOrderId, productId, payload) => {
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    this.itemIndexObjectStore.setObjectLoading(
      productIndex,
      PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
      true
    )
    try {
      const res: any = await this.service.bulkUpdateProductQty(
        clientOrderId,
        productId,
        payload,
        this.mapFiltersToIds(this.filterStore.appliedFilters),
        this.searchKey
      )
      const product = this.itemIndexObjectStore.idObjectMap.get(productId)
      const doorProducts = product.quantity.doorProducts
      payload.doorOrderWizeQuanties.forEach(({ doorOrderId, totalByDraftQuantity }) => {
        const doorProductQuantity =
          find(payload.doorOrderWizeQuanties, ['doorOrderId', doorOrderId])
            ?.sizeQuantities || []
        doorProducts[doorOrderId] = {
          ...doorProducts[doorOrderId],
          sizeQuantities: {
            ...doorProducts[doorOrderId]?.sizeQuantities,
            ...keyBy(doorProductQuantity, 'sizeId')
          },
          totalByDraftQuantity,
          totalBySizeQuantity: sumBy(doorProductQuantity, 'quantity')
        }
      })
      res.lineItems.forEach(lineItem => {
        doorProducts[lineItem.doorOrderId] = {
          ...doorProducts[lineItem.doorOrderId],
          totalValueByDraftQuantity: lineItem.totalValueByDraftQuantity,
          totalValueBySizeQuantity: lineItem.totalValueBySizeQuantity
        }
      })
      const updatedProduct = {
        ...product,
        quantity: {
          ...product.quantity,
          totalByDraftQuantity: res.product.totalByDraftQuantity,
          totalValueByDraftQuantity: res.product.totalValueByDraftQuantity || 0,
          totalBySizeQuantity: res.product.totalBySizeQuantity,
          totalValueBySizeQuantity: res.product.totalValueBySizeQuantity || 0,
          doorProducts
        }
      }
      this.itemIndexObjectStore.idObjectMap.set(product.id, updatedProduct)
      this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
      this.heirarchy = this.mergeHierarchies(
        res?.doorOrdersHierarchyKpi.productHierarchy.categories,
        this.heirarchy,
        res?.doorOrdersHierarchyKpi?.orderTotalKpis ?? {}
      )
      // Update Top Bar with updated KPI values
      const { clientOrder, doorOrder, doorOrders } = res
      orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({
        clientOrder,
        doorOrder,
        doorOrders
      })
      this.setQuantityUpdated(true)
    } catch (e) {
    } finally {
      this.itemIndexObjectStore.setObjectLoading(
        productIndex,
        PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
        false
      )
    }
  }

  @action
  updateComment = (
    buyingSessionProductId: string,
    comments: string,
    commentType: string
  ) => {
    const productIndex = this.itemIndexObjectStore.getIndexByKey(buyingSessionProductId)
    this.itemIndexObjectStore.setObjectLoading(
      productIndex,
      `${PRODUCT_OPERATIONS_LOADERS.UPDATING_DRAFT_QUANTITY}_${commentType}`,
      true
    )
    try {
      const {
        queryParams: { buyingSessionGroupId, clientId }
      } = nav
      this.commentService.updateComment(
        buyingSessionGroupId,
        buyingSessionProductId,
        clientId,
        {
          comments,
          commentType
        }
      )
      const product = this.itemIndexObjectStore.idObjectMap.get(buyingSessionProductId)
      const updatedProduct = {
        ...product,
        summary: {
          ...product.summary,
          productComments: { ...product.summary.productComments, [commentType]: comments }
        }
      }
      this.itemIndexObjectStore.idObjectMap.set(buyingSessionProductId, updatedProduct)
      this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
    } finally {
      this.itemIndexObjectStore.setObjectLoading(
        productIndex,
        `${PRODUCT_OPERATIONS_LOADERS.UPDATING_DRAFT_QUANTITY}_${commentType}`,
        false
      )
    }
  }

  @action
  async removeItemObjectFromHierarchy(
    index: number,
    resFromCache?: Record<string, any>,
    isProductRemoved?: Boolean
  ) {
    try {
      const res = await super.removeItemObjectFromHierarchy(
        index,
        resFromCache,
        isProductRemoved
      )
      const { clientOrder, doorOrder } = res
      orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder, doorOrder })
      this.heirarchy = isProductRemoved
        ? this.mergeHierarchies(
            res.hierarchyKPIs,
            this.heirarchy,
            res?.orderTotalKpis ?? {}
          )
        : this.heirarchy
      this.setQuantityUpdated(true)
    } catch (e) {
      throw e
    } finally {
      this.itemIndexObjectStore.setRangeLoading(
        index,
        index + 1,
        PRODUCT_REMOVE_LOADER_KEY,
        false
      )
    }
  }

  @action
  removeHierarchy(heirarchyStr: string[]) {
    this.heirarchy = [...this.heirarchy]
  }
}
